import { Label, makeStyles, shorthands, tokens, Button, Spinner } from '@fluentui/react-components';
import moment from 'moment';
import React, { FC, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IConversation } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import ConversationMenuItem from './ConversationMenuItem';
import { useUserPreferences } from '../../store/hooks';

interface MenuConversationsProps {
    items: Partial<IConversation>[];
    selectedItem?: IConversation;
    onRemoveConversation?: (id: string) => void;
    onConversationTitleChange?: (id: string, title: string) => void;
    onConversationSelected: (id: string) => void | Promise<void>;
    onPinToggle?: (id: string, pinned: boolean) => void;
    hidePinned?: boolean; // when true, do not render the pinned section (useful for search results)
    onLoadMore?: () => void; // callback to load more conversations
    hasMore?: boolean; // whether there are more conversations to load
    isLoadingMore?: boolean; // whether currently loading more conversations
    scrollContainerRef?: React.RefObject<HTMLDivElement>; // ref to the scroll container
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '0.25rem',
        flex: '1 1 auto',
        ...shorthands.gap('0.25rem'),
        height: '100%', // Take full height of container
        // Remove overflowY: 'auto' - let parent handle scrolling
    },
    group: {
        color: tokens.colorBrandForeground2,
        fontWeight: tokens.fontWeightBold,
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
    loadMoreButton: {
        width: '100%',
        minHeight: '2.5rem',
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
    },
    loadingSpinner: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: tokens.spacingVerticalL,
    },
});

const MenuConversations: FC<MenuConversationsProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { items, onRemoveConversation, onConversationTitleChange, onConversationSelected, onPinToggle, onLoadMore, hasMore, isLoadingMore, scrollContainerRef } = props;
    const { userPreferences } = useUserPreferences();
    const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

    // Compute pinned items (preserve user preference order)
    const pinnedItems = useMemo(() => {
        const pinnedIds: string[] = (userPreferences as any)?.pinnedConversations ?? [];
        if (!pinnedIds.length) return [] as Partial<IConversation>[];
        // Map pinned IDs to conversation objects, keeping order declared by user preferences
        return pinnedIds.map(id => items.find(it => it.id === id)).filter(Boolean) as Partial<IConversation>[];
    }, [userPreferences, items]);

    // Infinite scroll: trigger load more when the sentinel element comes into view
    useEffect(() => {
        if (!loadMoreTriggerRef.current || !hasMore || isLoadingMore || !onLoadMore) {
            return;
        }

        const scrollContainer = scrollContainerRef?.current;
        console.log('[MenuConversations] Setting up IntersectionObserver', {
            hasScrollContainer: !!scrollContainer,
            hasMore,
            isLoadingMore
        });

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                console.log('[MenuConversations] IntersectionObserver triggered', {
                    isIntersecting: entry.isIntersecting,
                    hasMore,
                    isLoadingMore
                });
                if (entry.isIntersecting && hasMore && !isLoadingMore) {
                    console.log('[MenuConversations] Loading more conversations...');
                    onLoadMore();
                }
            },
            {
                root: scrollContainer || null, // Use the scroll container if provided
                rootMargin: '200px', // trigger 200px before reaching the element
                threshold: 0,
            }
        );

        observer.observe(loadMoreTriggerRef.current);

        return () => {
            console.log('[MenuConversations] Cleaning up IntersectionObserver');
            observer.disconnect();
        };
    }, [hasMore, isLoadingMore, onLoadMore, scrollContainerRef]);

    // Don't sort - maintain the order from the store (which preserves pagination order)
    const sortedItems = items;

    return (
        <div className={classes.root}>
            {/* Pinned section on top (if any) */}
            {(!props.hidePinned) && pinnedItems.length > 0 && (
                <>
                    <Label className={classes.group} size='small'>
                        {t('menu.conversations.pinned')}
                    </Label>
                    {pinnedItems.map((item) => (
                        <ConversationMenuItem
                            key={item.id}
                            title={item.title || t('menu.conversations.new')}
                            id={item.id ?? ''}
                            isPinned={true}
                            onPinToggle={onPinToggle}
                            gptId={item.gpt?.id}
                            onRemoveClick={onRemoveConversation}
                            onTitleChange={onConversationTitleChange}
                            onSelect={onConversationSelected}
                            selected={props.selectedItem?.id === item.id}
                        />
                    ))}
                    <div style={{ 
                        height: '1px', 
                        backgroundColor: tokens.colorNeutralStroke2, 
                        margin: '4px 0',
                        flexShrink: 0
                    }} />
                </>
            )}

            {/* All conversations sorted by date, excluding pinned items to avoid duplicates */}
            {sortedItems
                .filter(item => !(userPreferences as any)?.pinnedConversations?.includes(item.id ?? ''))
                .map((item) => (
                    <ConversationMenuItem
                        key={item.id}
                        title={item.title || t('menu.conversations.new')}
                        id={item.id ?? ''}
                        isPinned={(userPreferences as any)?.pinnedConversations?.includes(item.id ?? '')}
                        onPinToggle={onPinToggle}
                        gptId={item.gpt?.id}
                        onRemoveClick={onRemoveConversation}
                        onTitleChange={onConversationTitleChange}
                        onSelect={onConversationSelected}
                        selected={props.selectedItem?.id === item.id}
                    />
                ))}
            
            {/* Infinite scroll trigger - invisible element that triggers load more */}
            {hasMore && onLoadMore && (
                <div ref={loadMoreTriggerRef} style={{ height: '1px' }} />
            )}
            
            {/* Loading spinner when fetching more */}
            {isLoadingMore && (
                <div className={classes.loadingSpinner}>
                    <Spinner size="small" label={t('menu.conversations.loading') || 'Loading...'} />
                </div>
            )}
        </div>
    );
};

export default MenuConversations;
